import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import React, { CSSProperties, FC } from "react";
import translate from "src/lib/translate";
import bgImage from "src/ui/assets/scale-image-shadow.webp";
import vecMask from "src/ui/assets/scale-vec-mask.svg";
import { getStreakMode, WEEKLY_STREAK_RAMP } from "./streakConfig";

const ProgressBar = styled.div`
  label: ProgressBar;
  width: 99px;
  aspect-ratio: 99/104;
  background-color: white;
  mask: url("${vecMask}");
  mask-size: cover;
  position: absolute;
  right: 27px;
  top: 13px;
`;

const WaveAnimation = keyframes`
  0% {
    trnsform: translateY(0);
  }
  25% {
    transform: translateY(1%);
  }
  50% {
    transform: translateY(0.5%);
  }
  75% {
    transform: translateY(2%);
  }
  100% {
    transform: translateY(0%);
  }
`;

const ProgressIndicatorGradient = styled.div`
  label: Gradient;
  height: 100%;
  width: 100%;
  position: relative;
  background-color: white;
  overflow: hidden;
  animation: ${WaveAnimation} 1.4s linear infinite alternate;

  html.partner-session & {
    background-color: var(--partner-10, #ededf5);
  }

  ::before {
    content: "";
    position: absolute;
    bottom: 0;
    background: white;
    width: 100%;
    --off-top: 20px;
    height: calc(var(--full-percent, 0%) + var(--off-top));
    background-image: linear-gradient(
      to bottom,
      #80aeff 0%,
      #f7bde6 50%,
      #ffbd70 100%
    );
    clip-path: path("M0 17C0 17 30 20 50 20C 72 20 99 17 99 17V 200H 0V 17 Z");
    transition: height 1s cubic-bezier(0.38, 0.7, 0.125, 1);
    transition-delay: 0.3s;

    html.partner-session & {
      background: var(--partner-10, #ededf5);
      background-image: linear-gradient(
        171deg,
        rgba(43, 38, 165, 0.15) 12.3%,
        rgba(57, 129, 231, 0.15) 81.83%
      );
    }
  }
`;

const BackDrop = styled.div`
  label: BackDrop;
  position: absolute;
  top: 0;
  left: 0;
  background-image: url(${bgImage});
  background-repeat: no-repeat;
  background-position: 0 0;
  width: 100%;
  aspect-ratio: 1/1;
`;

const ProgressText = styled.div`
  label: ProgressText;
  position: absolute;
  top: 60px;
  width: 99px;
  color: var(--color-charcoal);
  right: 27px;
  font-size: 1.625rem;
  text-align: center;
  font-weight: 500;
  letter-spacing: -0.045rem;
  pointer-events: all;

  span {
    font-size: 2.25rem;
  }

  html.partner-session & {
    background: linear-gradient(to bottom, #2b26a5, #3981e7);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const OffsetCenter = styled.div`
  position: relative;
  top: 0;
  left: 50%;
  width: 320px;
  transform: translate(-76%, 0);
  height: 100%;
  pointer-events: none;
`;

const ProgressWrap = styled.div`
  position: relative;
  width: 320px;
  height: 258px;
  height: 118px;
  margin-bottom: 1.5rem;
`;

const TOTAL_NUM = 7;

const ProgressIndicator: FC<{ count: number }> = (props) => {
  const { count } = props;

  let fillPercent: number;
  let label: string;
  let displayNode: React.ReactNode;

  if (getStreakMode() === "weekly") {
    const isExtended = count >= WEEKLY_STREAK_RAMP;
    fillPercent = isExtended
      ? 100
      : Math.round((count / WEEKLY_STREAK_RAMP) * 100);
    label = translate("weeklyStreak.aria.progress", { count });
    displayNode = isExtended ? (
      <span>{count}</span>
    ) : (
      <>
        <span>{count}</span>/{WEEKLY_STREAK_RAMP}
      </>
    );
  } else {
    fillPercent = Math.round((count / TOTAL_NUM) * 100);
    label = translate("dailyReadings.aria.progress", { count });
    displayNode = (
      <>
        <span>{count}</span>/{TOTAL_NUM}
      </>
    );
  }

  return (
    <OffsetCenter
      style={{ "--full-percent": `${fillPercent}%` } as CSSProperties}
    >
      <ProgressWrap>
        <BackDrop />
        <ProgressBar>
          <ProgressIndicatorGradient />
        </ProgressBar>
        <ProgressText aria-label={label} title={label}>
          {displayNode}
        </ProgressText>
      </ProgressWrap>
    </OffsetCenter>
  );
};

export default ProgressIndicator;
